/* eslint-disable no-nested-ternary */
import React from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import { Grid, Box, Stepper, Step, StepLabel, Tooltip } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import Typography from '@atoms/Typography';
import Spacer from '@atoms/Spacer';
import Button from '@atoms/Button';
import Skeleton from '@atoms/Skeleton';
import { redirectToRoute } from '@app/helpers';
import { FIELDS_ENUM, APPLICATION_STATUS } from '@app/constants';
import { useUser } from '@app/contexts/userContext';
import RoundedButton from '@app/components/atoms/RoundedButton';
import { colors } from '@app/config/colors';
import { OPPORTUNITY_TRACK_ROLE } from '@app/constants/roles';
import { useTranslation } from '@app/config/i18n';
import OpportunityCard from '../../opportunities/OpportunityCard';

import {
  Container,
  Header,
  Section,
  ApplicationMeta,
  SectionBox,
  StyledBox,
  StyledTitle,
} from './style';
import AssignReviewerForm from './AssignReviewerForm';
import ApplicationSubmissionSections from './ApplicationSubmissionSections';

const steps = ['Submitted', 'Reviewed', 'Final status'];

const ApplicationDetails = ({
  opportunity,
  application,
  reviewerView,
  refetch,
  opportunityMemberRole,
  sourcePage,
  previousSubmissionId,
  previousSubmissionApplicantName,
  nextSubmissionId,
  nextSubmissionApplicantName,
}) => {
  const { t } = useTranslation(['common', 'wizard', 'applications']);
  const router = useRouter();
  const { applications, page, perPage, listFilters } = router.query || {};
  const [showAssignReviewerForm, setShowAssignReviewerForm] = React.useState(false);
  const [activeStep] = React.useState(() => {
    if (APPLICATION_STATUS.REVIEWED === application?.status) {
      return 1;
    }
    if (
      APPLICATION_STATUS.ACCEPTED === application.status ||
      APPLICATION_STATUS.REJECTED === application.status
    ) {
      return 2;
    }
    return 0;
  });
  const { user } = useUser();

  const handleBackOpportunitySubmissions = () => {
    if (sourcePage) {
      redirectToRoute({}, 'opportunity_reviews', { params: { slug: opportunity.slug } });
    } else if (applications) {
      redirectToRoute({}, 'my_applications');
    } else {
      redirectToRoute({}, 'opportunity_submissions', { params: { slug: opportunity.slug } });
    }
  };

  const navigationQuery = {
    ...(page ? { page } : {}),
    ...(perPage ? { perPage } : {}),
    ...(listFilters ? { listFilters } : {}),
  };

  const handlePreviousSubmission = () => {
    if (!previousSubmissionId) {
      return;
    }

    redirectToRoute({}, 'view_submission', {
      params: { slug: opportunity.slug, id: previousSubmissionId },
      query: navigationQuery,
    });
  };

  const handleNextSubmission = () => {
    if (!nextSubmissionId) {
      return;
    }

    redirectToRoute({}, 'view_submission', {
      params: { slug: opportunity.slug, id: nextSubmissionId },
      query: navigationQuery,
    });
  };
  const applicationElements = application?.elements ?? [];
  let backButtonLabel = t('common:Back to Submission');
  const previousSubmissionTooltip = previousSubmissionApplicantName
    ? `Previous Submission: ${previousSubmissionApplicantName}`
    : 'Previous Submission';
  const nextSubmissionTooltip = nextSubmissionApplicantName
    ? `Next Submission: ${nextSubmissionApplicantName}`
    : 'Next Submission';

  if (applications) {
    backButtonLabel = t('common:back_to_all_sub');
  } else if (sourcePage) {
    backButtonLabel = t('common:Back to Review');
  }

  return (
    <>
      {!reviewerView && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              mt: '2.875rem',
            }}
          >
            <Button
              variant="text"
              onClick={handleBackOpportunitySubmissions}
              startIcon={<KeyboardArrowLeftRoundedIcon />}
            >
              {backButtonLabel}
            </Button>

            {(previousSubmissionId || nextSubmissionId) && !applications && !sourcePage && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1,
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                {previousSubmissionId && (
                  <Tooltip title={previousSubmissionTooltip} arrow>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handlePreviousSubmission}
                      startIcon={<KeyboardArrowLeftRoundedIcon />}
                      sx={{
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { sm: 'fit-content' },
                        justifyContent: 'center',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      Previous Submission
                    </Button>
                  </Tooltip>
                )}

                {nextSubmissionId && (
                  <Tooltip title={nextSubmissionTooltip} arrow>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleNextSubmission}
                      endIcon={<KeyboardArrowRightRoundedIcon />}
                      sx={{
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { sm: 'fit-content' },
                        justifyContent: 'center',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      Next Submission
                    </Button>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>

          <SectionBox display="flex" justifyContent="center">
            {applicationElements.map((field) =>
              field.type === FIELDS_ENUM.TITLE || field.type === FIELDS_ENUM.MANDATORY_TITLE ? (
                <ApplicationMeta
                  key={`${field.field_id ?? field.field_name ?? field.value ?? field.type}-title`}
                  variant="h5"
                  weight="600"
                >
                  {field.value?.toUpperCase()}
                </ApplicationMeta>
              ) : null
            )}
          </SectionBox>
        </>
      )}

      <SectionBox>
        <StyledTitle>
          <Typography
            sx={{ marginBottom: '4rem', textTransform: 'uppercase' }}
            variant="h5"
            color="secondary"
            gutterBottom
          >
            {t('common:sub_tracker')}
          </Typography>
        </StyledTitle>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </SectionBox>
      <ApplicationSubmissionSections elements={applicationElements} />

      {reviewerView
        ? null
        : application?.user?.id !== user?.id && (
            <SectionBox>
              <StyledTitle>
                <Typography
                  sx={{ marginBottom: '4rem', textTransform: 'uppercase' }}
                  variant="h5"
                  color="secondary"
                  gutterBottom
                >
                  {t('common:sub_rev')}
                </Typography>
              </StyledTitle>
              <Typography variant="h5" color="secondary" gutterBottom>
                {t('common:rev')}
              </Typography>
              {application.reviewers &&
                application.reviewers.length > 0 &&
                application.reviewers.map((reviewer) => (
                  <React.Fragment key={reviewer.id}>
                    <Spacer height="1rem" />
                    <Typography weight="600" variant="body1" color={colors.secondaryColor[900]}>
                      {reviewer.name}
                    </Typography>
                    <Typography variant="body1" color={colors.secondaryColor[500]} gutterBottom>
                      {reviewer.email}
                    </Typography>
                  </React.Fragment>
                ))}
              {showAssignReviewerForm ? (
                <AssignReviewerForm
                  opportunity={opportunity}
                  setShowAssignReviewerForm={setShowAssignReviewerForm}
                  refetch={refetch}
                  application={application}
                />
              ) : (
                <>
                  {opportunityMemberRole === OPPORTUNITY_TRACK_ROLE.ORGANIZER && (
                    <SectionBox>
                      <RoundedButton
                        sx={{
                          backgroundColor: colors.primaryColor[900],
                          px: '2rem',
                          py: '0.5rem',
                          marginLeft: '1rem',
                        }}
                        onClick={() => setShowAssignReviewerForm(true)}
                      >
                        {t('common:ass_rev')}
                      </RoundedButton>
                    </SectionBox>
                  )}
                </>
              )}
            </SectionBox>
          )}
    </>
  );
};

ApplicationDetails.propTypes = {
  application: PropTypes.object.isRequired,
  opportunity: PropTypes.object,
  reviewerView: PropTypes.bool,
  refetch: PropTypes.func.isRequired,
  opportunityMemberRole: PropTypes.string.isRequired,
  sourcePage: PropTypes.string,
  previousSubmissionId: PropTypes.string,
  previousSubmissionApplicantName: PropTypes.string,
  nextSubmissionId: PropTypes.string,
  nextSubmissionApplicantName: PropTypes.string,
};

ApplicationDetails.defaultProps = {
  reviewerView: false,
  opportunity: {},
  sourcePage: '',
  previousSubmissionId: null,
  previousSubmissionApplicantName: '',
  nextSubmissionId: null,
  nextSubmissionApplicantName: '',
};

ApplicationDetails.Skeleton = () => (
  <Container>
    <SectionBox>
      <Header container>
        <Skeleton animation="wave" variant="circle" height={20} width={20} />
      </Header>

      <ApplicationMeta variant="h6" weight="600">
        <Skeleton animation="wave" height={25} />
      </ApplicationMeta>

      <Section container spacing={3}>
        <Grid item sm={12} lg={9}>
          <Skeleton animation="wave" height={25} />
        </Grid>
        <Grid item lg={12}>
          <StyledBox>
            <LocationOnIcon />
            <Skeleton animation="wave" height={25} />
          </StyledBox>
        </Grid>
        <Grid item lg={12}>
          <StyledBox>
            <AlternateEmailIcon />
            <Skeleton animation="wave" height={25} />
          </StyledBox>
        </Grid>
      </Section>

      <Section container>
        {['skeleton-1', 'skeleton-2', 'skeleton-3', 'skeleton-4'].map((itemKey) => (
          <Grid item sm={12} key={itemKey}>
            <Skeleton animation="wave" height={50} margin="6px 0" />
          </Grid>
        ))}
      </Section>
    </SectionBox>
  </Container>
);

ApplicationDetails.fragments = {
  application: gql`
    fragment ApplicationDetails on ApplicationSubmission {
      id
      status
      created_at
      user {
        id
        name
        email
      }
      elements {
        # id
        field_id
        field_name
        type
        subtype
        value
        ... on ApplicationScalarElement {
          value
        }
        ... on ApplicationFilesElement {
          files {
            name
            path
            thumb
            mime_type
          }
        }
        child_elements {
          field_id
          field_name
          type
          subtype
          order
          value
        }
      }
      applicationReviews {
        id
        status
        created_at
        elements {
          field_name
          type
          subtype
          value
          ... on ApplicationScalarElement {
            value
            __typename
          }
          ... on ApplicationFilesElement {
            files {
              name
              path
              thumb
              mime_type
              __typename
            }
            __typename
          }
          child_elements {
            field_id
            field_name
            type
            subtype
            order
            value
            __typename
          }
          __typename
        }
        user {
          id
          name
        }
     }
      reviewers {
        id
        name
        email
      }
      opportunity {
        ...OpportunityCard
        organisation {
          name
          logo {
            thumb
          }
        }
      }
      track {
        id
        track_name
      }
    }
    ${OpportunityCard.fragments.opportunity}
  `,
};

export default ApplicationDetails;
